/**
 * LabelFold folds public app.aid.trust.label events by subject (moderation spec
 * §6, trust §5.3). Mirrors services/corroboration/label.go: weight ∈ (0,1],
 * latest-per-subject wins, an absent label → 1.0. The Go fold owns ranking
 * dampening inside corroboration W; this client fold powers the You-tab
 * transparency surface (§7 "an identity can see the labels/actions against it")
 * and any client-side downrank hint. Subjects are tid-or-did.
 */
export interface LabelAction {
  /** TID of the label event itself — the `action` an appeal references (§7). */
  tid: string;
  subject: string;
  weight: number;
  reason: string;
  issuer: string;
  expiresAt?: string;
}

// Floor matching the Go fold's PresenceLiftMin intent: a label shrinks, never
// silences (full silencing is a suppression action, not a label).
const WEIGHT_FLOOR = 0.05;

type FoldEvent = { id?: string; $type?: string; identityId?: string; payload?: Record<string, unknown> };

export class LabelFold {
  #latest = new Map<string, LabelAction>();

  observe(ev: FoldEvent): void {
    if (ev?.$type !== "app.aid.trust.label") return;
    const p = ev.payload;
    if (!p) return;
    const subject = typeof p.subject === "string" ? p.subject : "";
    if (!subject) return;
    let weight = typeof p.weight === "number" ? p.weight : NaN;
    if (Number.isNaN(weight)) return;
    if (weight <= 0) weight = WEIGHT_FLOOR;
    if (weight > 1) weight = 1;
    const action: LabelAction = {
      tid: typeof ev.id === "string" ? ev.id : "",
      subject,
      weight,
      reason: typeof p.reason === "string" ? p.reason : "other",
      issuer: typeof ev.identityId === "string" ? ev.identityId : "",
    };
    if (typeof p.expiresAt === "string") action.expiresAt = p.expiresAt;
    this.#latest.set(subject, action); // latest-per-subject wins (seq order)
  }

  dampening(subject: string): number {
    return this.#latest.get(subject)?.weight ?? 1.0;
  }

  actionsAgainst(subject: string): LabelAction[] {
    const a = this.#latest.get(subject);
    return a ? [a] : [];
  }
}
